import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Megaphone,
  Navigation,
  Image,
  Shield,
  Save,
  Loader2,
  Phone,
  Mail,
  Clock,
  Truck,
  Award,
  Headphones,
  Lock,
  Upload,
  X,
  Eye,
  Palette,
  Type,
  Plus,
  Pencil,
  Trash2,
  GripVertical
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getImageUrl } from "../../utils/imageUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminStoreAppearance() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [settings, setSettings] = useState({
    // Announcement Bar
    announcement_enabled: false,
    announcement_text: "",

    // Top Navigation (Platform Provider Info)
    top_nav_phone: "",
    top_nav_email: "",
    top_nav_hours: "",

    // Hero Banner
    hero_headline: "",
    hero_subheadline: "",
    hero_cta_text: "",
    hero_cta_link: "",
    hero_image_url: "",

    // Trust Badges / Quality Text
    trust_free_shipping: "Free Shipping on Orders Over $500",
    trust_quality_guarantee: "100% Quality Guaranteed",
    trust_customer_support: "24/7 Customer Support",
    trust_secure_payment: "Secure Payment",
    footer_copyright_text: "",
    banners: []
  });

  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerFormData, setBannerFormData] = useState({
    title: "",
    description: "",
    link: "",
    cta_text: "Shop Now",
    image_url: "",
    is_active: true
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/store-appearance`, getAuthHeaders());
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Failed to load settings:", error);
    } finally {
      setLoading(false);
    }
  };


  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleAddBanner = () => {
    setEditingBanner(null);
    setBannerFormData({
      title: "",
      description: "",
      link: "",
      cta_text: "Shop Now",
      image_url: "",
      is_active: true
    });
    setBannerDialogOpen(true);
  };

  const handleEditBanner = (index) => {
    setEditingBanner(index);
    setBannerFormData({ ...settings.banners[index] });
    setBannerDialogOpen(true);
  };

  const handleDeleteBanner = (index) => {
    if (window.confirm("Are you sure you want to delete this banner?")) {
      const updatedBanners = [...settings.banners];
      updatedBanners.splice(index, 1);

      setSettings(prev => {
        const updatedSettings = { ...prev, banners: updatedBanners };
        saveSettings(updatedSettings);
        return updatedSettings;
      });
      toast.success("Banner deleted!");
    }
  };

  const handleSaveBanner = () => {
    if (!bannerFormData.image_url) {
      toast.error("Please upload a banner image");
      return;
    }

    const updatedBanners = [...settings.banners];
    if (editingBanner !== null) {
      updatedBanners[editingBanner] = bannerFormData;
    } else {
      updatedBanners.push(bannerFormData);
    }

    setSettings(prev => {
      const updatedSettings = { ...prev, banners: updatedBanners };
      // Auto-save to persistence
      saveSettings(updatedSettings);
      return updatedSettings;
    });

    setBannerDialogOpen(false);
    toast.success(editingBanner !== null ? "Banner updated!" : "Banner added!");
  };

  const saveSettings = async (settingsToSave) => {
    setSaving(true);
    try {
      const payload = settingsToSave || settings;
      await axios.put(`${API}/admin/store-appearance`, payload, getAuthHeaders());
      toast.success("Appearance settings updated! Reloading to apply changes...");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error("Failed to save changes to database");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => saveSettings();

  const handleBannerImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('hero', file);

    try {
      const response = await axios.post(`${API}/upload/hero`, formData, {
        ...getAuthHeaders()
      });
      setBannerFormData(prev => ({ ...prev, image_url: response.data.url }));
      toast.success("Image uploaded!");
    } catch (error) {
      toast.error("Failed to upload banner image");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-store-appearance">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Store Appearance
          </h1>
          <p className="text-slate-500">Manage global content displayed on all reseller stores</p>
        </div>

      </div>

      {/* Global Theme Settings */}
      <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Palette className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900">Global Theme Defaults</h3>
            <p className="text-sm text-slate-500">Base colors and typography for all stores (resellers can override these)</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Palette className="w-4 h-4 text-slate-400" />
              Primary Color
            </Label>
            <div className="flex gap-3">
              <Input
                type="color"
                value={settings.primary_color || "#000000"}
                onChange={(e) => updateSetting("primary_color", e.target.value)}
                className="w-12 h-10 p-1 rounded-md cursor-pointer"
              />
              <Input
                type="text"
                value={settings.primary_color || "#000000"}
                onChange={(e) => updateSetting("primary_color", e.target.value)}
                placeholder="#000000"
                className="flex-1 font-mono"
              />
            </div>
            <p className="text-xs text-slate-400">Used as default primary color for buttons and highlights</p>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Type className="w-4 h-4 text-slate-400" />
              Primary Font
            </Label>
            <Select
              value={(settings.font || "Inter").split(',')[0].replace(/['"]/g, '').trim()}
              onValueChange={(v) => updateSetting("font", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select font" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Inter">Inter (Modern Sans)</SelectItem>
                <SelectItem value="Roboto">Roboto</SelectItem>
                <SelectItem value="Outfit">Outfit (Premium Sans)</SelectItem>
                <SelectItem value="Playfair Display">Playfair Display (Elegant Serif)</SelectItem>
                <SelectItem value="Lora">Lora (Classic Serif)</SelectItem>
                <SelectItem value="Cormorant Garamond">Cormorant Garamond (Urn Shop Standard)</SelectItem>
                <SelectItem value="Montserrat">Montserrat</SelectItem>
                <SelectItem value="Arial">Arial</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">Default typography across all storefronts</p>
          </div>
        </div>
      </div>

      {/* Announcement Bar */}
      <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
            <Megaphone className="w-5 h-5 text-orange-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900">Announcement Bar</h3>
            <p className="text-sm text-slate-500">Display a promotional message at the top of all stores</p>
          </div>
          <Switch
            checked={settings.announcement_enabled}
            onCheckedChange={(v) => updateSetting("announcement_enabled", v)}
            data-testid="announcement-toggle"
          />
        </div>

        {settings.announcement_enabled && (
          <div className="pt-4 border-t">
            <Label>Announcement Text</Label>
            <Input
              value={settings.announcement_text}
              onChange={(e) => updateSetting("announcement_text", e.target.value)}
              placeholder="e.g., Free Shipping on Orders Over $500! Use code FREESHIP"
              className="mt-1"
              data-testid="announcement-text"
            />
            <p className="text-xs text-slate-400 mt-1">Color will match each store's theme accent color</p>
          </div>
        )}
      </div>

      {/* Top Navigation */}
      <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Navigation className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Top Navigation Bar</h3>
            <p className="text-sm text-slate-500">Platform provider contact info shown on all stores</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
          <div>
            <Label className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-slate-400" />
              Phone Number
            </Label>
            <Input
              value={settings.top_nav_phone}
              onChange={(e) => updateSetting("top_nav_phone", e.target.value)}
              placeholder="1-800-555-0123"
              className="mt-1"
              data-testid="top-nav-phone"
            />
          </div>
          <div>
            <Label className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-slate-400" />
              Email
            </Label>
            <Input
              value={settings.top_nav_email}
              onChange={(e) => updateSetting("top_nav_email", e.target.value)}
              placeholder="support@example.com"
              className="mt-1"
              data-testid="top-nav-email"
            />
          </div>
          <div>
            <Label className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-400" />
              Business Hours
            </Label>
            <Input
              value={settings.top_nav_hours}
              onChange={(e) => updateSetting("top_nav_hours", e.target.value)}
              placeholder="Mon-Fri 9AM-5PM EST"
              className="mt-1"
              data-testid="top-nav-hours"
            />
          </div>
        </div>
      </div>

      {/* Banner Management */}
      <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Image className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Banner Management</h3>
              <p className="text-sm text-slate-500">Manage multiple hero banners for store homepages</p>
            </div>
          </div>
          <Button
            onClick={handleAddBanner}
            variant="outline"
            className="gap-2 border-primary/20 text-primary hover:bg-primary/10"
          >
            <Plus className="w-4 h-4" />
            Add Banner
          </Button>
        </div>

        <div className="pt-4 border-t">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Image</TableHead>
                <TableHead>Content</TableHead>
                <TableHead>Link</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {settings.banners?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No banners added yet.
                  </TableCell>
                </TableRow>
              ) : (
                settings.banners?.map((banner, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <img src={getImageUrl(banner.image_url)} alt="" className="w-16 h-10 object-cover rounded border" />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900">{banner.title || 'No Title'}</div>
                      <div className="text-xs text-slate-500 truncate max-w-[200px]">{banner.description || 'No description'}</div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500 font-mono">
                      {banner.link || '#'}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={banner.is_active}
                        onCheckedChange={(v) => {
                          const updatedBanners = [...settings.banners];
                          updatedBanners[index].is_active = v;
                          setSettings(prev => {
                            const updatedSettings = { ...prev, banners: updatedBanners };
                            saveSettings(updatedSettings);
                            return updatedSettings;
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditBanner(index)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteBanner(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Banner Modal */}
      <Dialog open={bannerDialogOpen} onOpenChange={setBannerDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingBanner !== null ? 'Edit Banner' : 'Add New Banner'}</DialogTitle>
            <DialogDescription>
              Banners are displayed in a carousel on all store homepages.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Banner Image *</Label>
              <div className="relative group">
                {bannerFormData.image_url ? (
                  <div className="relative rounded-lg overflow-hidden border">
                    <img src={getImageUrl(bannerFormData.image_url)} alt="" className="w-full h-32 object-cover" />
                    <button
                      onClick={() => setBannerFormData(prev => ({ ...prev, image_url: "" }))}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-lg cursor-pointer hover:border-purple-400">
                    <input type="file" className="hidden" accept="image/*" onChange={handleBannerImageUpload} disabled={uploading} />
                    {uploading ? <Loader2 className="w-6 h-6 animate-spin text-purple-600" /> : <Upload className="w-6 h-6 text-slate-400" />}
                    <span className="text-xs text-slate-500 mt-2">Click to upload banner</span>
                  </label>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={bannerFormData.title}
                onChange={(e) => setBannerFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g. Summer Collection"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={bannerFormData.description}
                onChange={(e) => setBannerFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="A short description for the banner"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Link</Label>
                <Input
                  value={bannerFormData.link}
                  onChange={(e) => setBannerFormData(prev => ({ ...prev, link: e.target.value }))}
                  placeholder="#products or https://..."
                />
              </div>
              <div className="space-y-2">
                <Label>CTA Text</Label>
                <Input
                  value={bannerFormData.cta_text}
                  onChange={(e) => setBannerFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                  placeholder="Shop Now"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBannerDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveBanner} className="bg-purple-600 hover:bg-purple-700">
              {editingBanner !== null ? 'Update Banner' : 'Add Banner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trust Badges / Quality Text */}
      <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-green-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900">Trust Badges & Quality Text</h3>
            <p className="text-sm text-slate-500">Reassurance text displayed across all stores</p>
          </div>
          <Switch
            checked={settings.trust_badges_enabled !== false}
            onCheckedChange={(v) => updateSetting("trust_badges_enabled", v)}
          />
        </div>

        {settings.trust_badges_enabled !== false && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
            <div>
              <Label className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-slate-400" />
                Free Shipping Text
              </Label>
              <Input
                value={settings.trust_free_shipping}
                onChange={(e) => updateSetting("trust_free_shipping", e.target.value)}
                placeholder="Free Shipping on Orders Over $500"
                className="mt-1"
                data-testid="trust-free-shipping"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Award className="w-4 h-4 text-slate-400" />
                Quality Guarantee Text
              </Label>
              <Input
                value={settings.trust_quality_guarantee}
                onChange={(e) => updateSetting("trust_quality_guarantee", e.target.value)}
                placeholder="100% Quality Guaranteed"
                className="mt-1"
                data-testid="trust-quality-guarantee"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Headphones className="w-4 h-4 text-slate-400" />
                Customer Support Text
              </Label>
              <Input
                value={settings.trust_customer_support}
                onChange={(e) => updateSetting("trust_customer_support", e.target.value)}
                placeholder="24/7 Customer Support"
                className="mt-1"
                data-testid="trust-customer-support"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-slate-400" />
                Secure Payment Text
              </Label>
              <Input
                value={settings.trust_secure_payment}
                onChange={(e) => updateSetting("trust_secure_payment", e.target.value)}
                placeholder="Secure Payment"
                className="mt-1"
                data-testid="trust-secure-payment"
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer Management */}
      <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
            <Lock className="w-5 h-5 text-slate-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Footer Management</h3>
            <p className="text-sm text-slate-500">Manage copyright and credits text in the site footer</p>
          </div>
        </div>

        <div className="pt-4 border-t">
          <Label>Copyright Text</Label>
          <Input
            value={settings.footer_copyright_text}
            onChange={(e) => updateSetting("footer_copyright_text", e.target.value)}
            placeholder="e.g., © 2026 eDisplayroom by Infinity Funeral Supply. All rights reserved."
            className="mt-1"
            data-testid="footer-copyright"
          />
          <p className="text-xs text-slate-400 mt-1">Custom copyright line for the site footer</p>
        </div>
      </div>

      {/* Preview Note */}
      <div className="bg-primary/5 border border-primary/10 rounded-xl p-4 flex items-start gap-3">
        <Eye className="w-5 h-5 text-primary mt-0.5" />
        <div>
          <p className="text-sm font-medium text-primary">Preview Changes</p>
          <p className="text-sm text-primary/80">
            After saving, visit any reseller store to see these changes applied. Colors will adapt to each store's theme.
          </p>
        </div>
      </div>

      {/* Footer Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-primary hover:bg-primary/90 gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </Button>
      </div>
    </div>
  );
}
